import { Alpine as AlpineType } from 'alpinejs';

// Predictive Search Lazy Loader
let predictiveSearchLoaded = false;

const loadPredictiveSearch = async (): Promise<void> => {
  if (!predictiveSearchLoaded) {
    predictiveSearchLoaded = true;
    await import('../../assets/lsg-predictive-search.js');
  }
};

export const setupPredictiveSearchLazyLoading = (): void => {
  const triggerSelector = '.js-lsg-modal-trigger';
  
  const isElementTarget = (target: EventTarget | null): target is Element => {
    return target instanceof Element;
  };
  
  // Desktop: preload on hover
  document.addEventListener('mouseenter', (e) => {
    if (isElementTarget(e.target) && e.target.closest(triggerSelector)) {
      loadPredictiveSearch();
    }
  }, true);

  // Keyboard navigation: preload on focus
  document.addEventListener('focusin', (e) => {
    if (isElementTarget(e.target) && e.target.closest(triggerSelector)) {
      loadPredictiveSearch();
    }
  });

  // Mobile: preload on touchstart (before click)
  document.addEventListener('touchstart', (e) => {
    if (isElementTarget(e.target) && e.target.closest(triggerSelector)) {
      loadPredictiveSearch();
    }
  }, { passive: true });

  // Fallback: load on click if not already loaded
  document.addEventListener('click', async (e) => {
    const target = isElementTarget(e.target) ? e.target.closest(triggerSelector) : null;
    if (target && !predictiveSearchLoaded) {
      e.preventDefault();
      await loadPredictiveSearch();
      // Re-trigger the click after script is loaded
      target.click();
    }
  });
};

// Component Lazy Loaders
export const loadProductReviews = async () => {
  if (window.template && window.template.includes('product')) {
    const { default: ProductReviewsModule } = await import('~/components/product-reviews/index');
    return ProductReviewsModule;
  }
  return null;
};

export const loadStickyAddToCart = async () => {
  if (window.template && window.template.includes('product')) {
    const { default: StickyAddToCartModule } = await import('@/components/sticky-add-to-cart');
    return StickyAddToCartModule;
  }
  return null;
};

export const loadMobileMenu = async (Alpine: AlpineType) => {
  if (window.innerWidth <= 1023) {
    const { default: MobileMenuModule } = await import('@/components/mobile-menu');
    return MobileMenuModule;
  } else {
    // Fake MobileMenu for desktop to prevent Alpine errors
    return (Alpine: AlpineType) => {
      Alpine.data('mobileMenu', () => ({
        isOpen: false,
        toggle() {},
        open() {},
        close() {}
      }));
      Alpine.data('isOpen', () => false);
    };
  }
};

export const loadLSGProductView = async () => {
  if (document.querySelector('[x-data*="LSGProductView"]')) {
    const { default: LSGProductViewModule } = await import('@/components/lsg-product-view');
    return LSGProductViewModule;
  }
  return null;
};

// LSG Bundle Lazy Loader
export const setupLSGBundleLazyLoading = (): void => {
  const checkForLSGBundle = () => {
    if (document.querySelector('lsg-bundle')) {
      import('@/components/lsg-bundle');
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', checkForLSGBundle);
  } else {
    checkForLSGBundle();
  }
};